import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Download, Eye, X, ArrowRight, Sparkles, Award, TrendingUp, Home, Clock, Heart, Gem, Loader2, Cpu, Wifi, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { API, api, formatApiError } from "../lib/api";

const ease = [0.16, 1, 0.3, 1];

const PILLARS = [
  { n: "01", t: "Todo Integrado", d: "Un ecosistema completo — diseño, mobiliario, iluminación y equipamiento — en una sola solución." },
  { n: "02", t: "Todo Financiado", d: "Soluciones de financiamiento integradas para que el cliente acceda sin barreras al hogar completo." },
  { n: "03", t: "Todo Listo", d: "El hogar entregado en condiciones de habitabilidad inmediata. Sin esperas. Sin pendientes." },
];

const CLIENT = [
  { icon: Sparkles, t: "Menos estrés", d: "Sin coordinaciones interminables. Todo ya está resuelto." },
  { icon: Clock, t: "Menos tiempo", d: "Del contrato al hogar, sin esperas ni compras adicionales." },
  { icon: Heart, t: "Más felicidad", d: "El cliente llega. Y ya es feliz." },
  { icon: Gem, t: "Más valor", d: "Un hogar listo y equipado vale más — en precio y en emoción." },
];

const ADVISOR = [
  "Más argumentos: narrativas completas con diseño, mobiliario y experiencia de vida.",
  "Mayor diferenciación: una casa lista para vivir desde el primer día.",
  "Más cierres: la experiencia inmersiva reduce la indecisión.",
  "Clientes más felices: un cliente satisfecho regresa y recomienda.",
];

const ROADMAP = [
  { t: "GOÛT READY Academy™", d: "Formación certificada en ventas experienciales, storytelling y herramientas digitales." },
  { t: "Acceso a la plataforma", d: "Activación del recorrido 360° Virtual Experience como ventaja competitiva." },
  { t: "Primeras operaciones", d: "Primeras ventas GOÛT READY™ y activación del Success Reward." },
  { t: "Programa Elite", d: "Objetivo mínimo de 3 ventas para acceso continuo y beneficios avanzados." },
  { t: "GOÛT READY Ambassador™", d: "El nivel más alto: reconocimiento, beneficios exclusivos y comunidad de élite." },
];

const TIERS = [
  { t: "Silver", d: ["Herramientas base", "Capacitación GOÛT Academy", "Perfil en directorio oficial"] },
  { t: "Gold", d: ["Todo lo anterior", "Prioridad en nuevos proyectos", "Eventos exclusivos GOÛT"] },
  { t: "Platinum", d: ["Todo lo anterior", "Networking internacional", "Acceso anticipado a colecciones"] },
  { t: "Black", d: ["Todo lo anterior", "Embajador oficial", "Co-branding", "GOÛT Black Circle"] },
];

const ECOSYSTEM = [
  { b: "GOÛT", d: "Design Intelligence · Experiencia, Tecnología, Plataforma" },
  { b: "VETTA", d: "Manufacturing & Distribution · Fabricación, Logística, Escala" },
  { b: "AURA", d: "Lifestyle & Living · Productos, Equipamiento, Estilo" },
];

const Reveal = ({ children, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, ease, delay }}>{children}</motion.div>
);

export default function GoutReady() {
  const [docs, setDocs] = useState([]);
  const [modalDoc, setModalDoc] = useState(null);
  const [viewer, setViewer] = useState(null); // {token, title}

  useEffect(() => { window.scrollTo(0, 0); api.get("/gout-ready/docs").then((r) => setDocs(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-cream" data-testid="gout-ready-page">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 px-6 lg:px-10 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="overline text-violet mb-4">GOÛT · Design Intelligence</div>
            <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl tracking-tight text-navy leading-[1.05]">
              GOÛT READY™
            </h1>
            <p className="mt-5 text-lg md:text-xl text-navy/60 max-w-2xl">The Move-In Intelligence Platform.</p>
            <p className="mt-3 font-editorial text-2xl md:text-3xl text-navy tracking-tight">Sell more. Earn more.<br />Create better experiences.</p>
            <a href="#presentaciones" data-testid="hero-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white px-6 py-3.5 hover:opacity-90 transition-opacity">
              Ver la presentación <ArrowRight className="w-4 h-4" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* El modelo */}
      <section className="py-20 px-6 lg:px-10 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <Reveal><h2 className="font-editorial text-3xl md:text-4xl tracking-tight text-navy">El modelo · Los tres pilares</h2></Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <div className="bg-cream border border-navy/10 rounded-2xl p-8 h-full">
                  <div className="font-editorial text-5xl text-violet/30">{p.n}</div>
                  <h3 className="mt-4 text-xl font-medium text-navy">{p.t}</h3>
                  <p className="mt-2 text-navy/60 text-[15px] leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Para el cliente */}
      <section className="py-20 px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          <Reveal><h2 className="font-editorial text-3xl md:text-4xl tracking-tight text-navy">Para el cliente</h2>
            <p className="mt-3 text-navy/55 text-lg">Un hogar listo para vivir cambia todo.</p></Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CLIENT.map((c, i) => (
              <Reveal key={c.t} delay={i * 0.06}>
                <div className="h-full">
                  <div className="w-11 h-11 rounded-xl bg-violet/10 text-violet flex items-center justify-center"><c.icon className="w-5 h-5" /></div>
                  <h3 className="mt-4 text-lg font-medium text-navy">{c.t}</h3>
                  <p className="mt-1.5 text-navy/60 text-sm leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Casas inteligentes · cerebro tecnológico */}
      <section className="py-24 px-6 lg:px-10 bg-white/50 overflow-hidden" data-testid="gr-smart-homes">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-violet opacity-10 blur-3xl rounded-[2rem]" />
              <img src="https://static.prod-images.emergentagent.com/jobs/b793e25b-95c0-45a5-84ac-f1c005ccccb0/images/53bf171d2ee83b63efeda17378e7b2a9b7ac047f9ef5d518d1c0f00bc0a999cd.png"
                alt="Casa inteligente GOÛT con cerebro tecnológico"
                className="relative rounded-2xl w-full object-cover shadow-2xl border border-navy/10" data-testid="gr-smart-home-img" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex items-center gap-2 text-violet"><Cpu className="w-5 h-5" /><span className="overline">Living Intelligence</span></div>
            <h2 className="mt-3 font-editorial text-3xl md:text-4xl tracking-tight text-navy leading-[1.1]">
              Casas inteligentes de última generación con cerebro tecnológico.
            </h2>
            <p className="mt-5 text-navy/60 text-[17px] leading-relaxed">
              Desarrollamos hogares que piensan. Cada casa integra un <span className="text-navy font-medium">cerebro tecnológico</span> que
              aprende y automatiza la vida diaria — iluminación, clima, seguridad, energía y experiencias — para que el hogar
              se adapte a ti, y no al revés. Tecnología de vanguardia al servicio del confort, el ahorro y el bienestar.
            </p>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {[
                { icon: Cpu, t: "Cerebro central", d: "Un sistema que orquesta toda la casa de forma autónoma." },
                { icon: Lightbulb, t: "Vida automatizada", d: "Rutinas que se anticipan a tus necesidades cada día." },
                { icon: Wifi, t: "Todo conectado", d: "Control total desde una sola experiencia, en cualquier lugar." },
              ].map((x) => (
                <div key={x.t}>
                  <div className="w-10 h-10 rounded-xl bg-violet/10 text-violet flex items-center justify-center"><x.icon className="w-5 h-5" /></div>
                  <h3 className="mt-3 text-[15px] font-medium text-navy">{x.t}</h3>
                  <p className="mt-1 text-navy/55 text-[13px] leading-relaxed">{x.d}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Para el asesor + recompensas */}
      <section className="py-20 px-6 lg:px-10 bg-navy text-cream grain">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <div className="overline text-cream/40">Para el asesor inmobiliario</div>
            <h2 className="mt-3 font-editorial text-3xl md:text-4xl tracking-tight">De vendedor a consultor de experiencias.</h2>
            <ul className="mt-8 space-y-4">
              {ADVISOR.map((a) => (
                <li key={a} className="flex gap-3 text-cream/75"><TrendingUp className="w-5 h-5 text-gold shrink-0 mt-0.5" /><span>{a}</span></li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-4">
              <div className="rounded-2xl border border-cream/15 bg-cream/5 p-8">
                <div className="font-editorial text-5xl text-gold">3.5%</div>
                <div className="mt-2 text-lg font-medium">Success Reward</div>
                <p className="mt-1 text-cream/60 text-sm">Reconocimiento económico por cada operación GOÛT READY exitosa sobre el valor total del paquete.</p>
              </div>
              <div className="rounded-2xl border border-cream/15 bg-cream/5 p-8">
                <div className="font-editorial text-5xl text-violet">1.5%</div>
                <div className="mt-2 text-lg font-medium">Marketing Partner Reward</div>
                <p className="mt-1 text-cream/60 text-sm">Para equipos de marketing que impulsen activamente la iniciativa GOÛT READY™.</p>
              </div>
              <p className="text-[11px] text-cream/35">Sujeto a convenio con cada desarrollador, políticas internas y regulaciones aplicables.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Roadmap */}
      <section className="py-20 px-6 lg:px-10">
        <div className="max-w-5xl mx-auto">
          <Reveal><h2 className="font-editorial text-3xl md:text-4xl tracking-tight text-navy">Roadmap del Partner Certificado</h2></Reveal>
          <div className="mt-12 space-y-4">
            {ROADMAP.map((r, i) => (
              <Reveal key={r.t} delay={i * 0.05}>
                <div className="flex gap-5 items-start bg-white/60 border border-navy/10 rounded-xl p-6">
                  <div className="font-editorial text-2xl text-violet w-10 shrink-0">0{i + 1}</div>
                  <div><h3 className="text-lg font-medium text-navy">{r.t}</h3><p className="text-navy/60 text-sm mt-1">{r.d}</p></div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ambassador tiers */}
      <section className="py-20 px-6 lg:px-10 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <Reveal><div className="flex items-center gap-2 text-violet"><Award className="w-5 h-5" /><span className="overline">GOÛT READY Ambassador™</span></div>
            <h2 className="mt-3 font-editorial text-3xl md:text-4xl tracking-tight text-navy">Cuatro niveles de excelencia</h2></Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TIERS.map((t, i) => (
              <Reveal key={t.t} delay={i * 0.06}>
                <div className="bg-cream border border-navy/10 rounded-2xl p-7 h-full">
                  <h3 className="text-xl font-medium text-navy">{t.t}</h3>
                  <ul className="mt-4 space-y-2">
                    {t.d.map((x) => <li key={x} className="text-navy/60 text-sm flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full bg-violet shrink-0" />{x}</li>)}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Presentaciones (password-gated) */}
      <section id="presentaciones" className="py-20 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="flex items-center gap-2 text-navy/50"><Lock className="w-4 h-4" /><span className="overline">Acceso protegido</span></div>
            <h2 className="mt-3 font-editorial text-3xl md:text-4xl tracking-tight text-navy">Presentaciones GOÛT READY™</h2>
            <p className="mt-3 text-navy/55">Solicita la contraseña de acceso a nuestro equipo para ver o descargar la presentación completa.</p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            {docs.map((d, i) => (
              <Reveal key={d.id} delay={i * 0.08}>
                <div data-testid={`gr-card-${d.id}`} className="bg-navy text-cream rounded-2xl p-8 h-full flex flex-col">
                  <div className="w-11 h-11 rounded-xl bg-cream/10 flex items-center justify-center"><Lock className="w-5 h-5 text-gold" /></div>
                  <h3 className="mt-5 text-xl font-medium">{d.title}</h3>
                  <p className="mt-1.5 text-cream/55 text-sm flex-1">{d.subtitle}</p>
                  <button data-testid={`gr-unlock-${d.id}`} onClick={() => setModalDoc(d)}
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-violet text-white px-5 py-3 text-sm hover:opacity-90 transition-opacity">
                    <Eye className="w-4 h-4" /> Ver / descargar
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ecosistema */}
      <section className="py-20 px-6 lg:px-10 bg-white/50">
        <div className="max-w-6xl mx-auto text-center">
          <Reveal><div className="overline text-navy/40">The Intelligence Ecosystem</div>
            <h2 className="mt-3 font-editorial text-3xl md:text-4xl tracking-tight text-navy">Cuatro marcas. Una inteligencia.</h2></Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6 text-left">
            {ECOSYSTEM.map((e, i) => (
              <Reveal key={e.b} delay={i * 0.08}>
                <div className="border border-navy/10 rounded-2xl p-8 bg-cream">
                  <div className="font-editorial text-2xl text-navy tracking-tight">{e.b}</div>
                  <p className="mt-2 text-navy/55 text-sm">{e.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Password modal */}
      <AnimatePresence>
        {modalDoc && <PasswordModal doc={modalDoc} onClose={() => setModalDoc(null)}
          onUnlock={(token, title, hasPptx) => { setModalDoc(null); setViewer({ token, title, hasPptx }); }} />}
      </AnimatePresence>

      {/* Viewer */}
      <AnimatePresence>
        {viewer && <PdfViewer viewer={viewer} onClose={() => setViewer(null)} />}
      </AnimatePresence>
    </div>
  );
}

function PasswordModal({ doc, onClose, onUnlock }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/gout-ready/${doc.id}/verify`, { password: pw });
      toast.success("Acceso concedido");
      onUnlock(data.token, data.title, data.has_pptx);
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Contraseña incorrecta");
    } finally { setLoading(false); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-navy/50 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} transition={{ duration: 0.4, ease }}
        onClick={(e) => e.stopPropagation()} data-testid="gr-password-modal" className="w-full max-w-md bg-cream rounded-2xl p-8">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2 text-navy"><Lock className="w-5 h-5 text-violet" /><span className="font-medium">Acceso protegido</span></div>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-navy/60">{doc.title} — ingresa la contraseña que te compartió el equipo GOÛT.</p>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <input type="password" autoFocus required value={pw} onChange={(e) => setPw(e.target.value)}
            data-testid="gr-password-input" placeholder="Contraseña"
            className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
          <button type="submit" disabled={loading} data-testid="gr-password-submit"
            className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity disabled:opacity-50">
            {loading ? "Verificando…" : "Desbloquear"}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}

function PdfViewer({ viewer, onClose }) {
  const pdfUrl = `${API}/gout-ready/file/${viewer.token}?fmt=pdf`;
  const [loaded, setLoaded] = useState(false);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-navy/90 backdrop-blur-sm flex flex-col" data-testid="gr-viewer">
      <div className="flex items-center justify-between px-5 py-3 text-cream">
        <span className="font-medium truncate">{viewer.title}</span>
        <div className="flex items-center gap-2">
          <a href={`${pdfUrl}&download=1`} data-testid="gr-download-pdf"
            className="inline-flex items-center gap-2 rounded-full bg-cream/10 hover:bg-cream/20 px-4 py-2 text-sm transition-colors"><Download className="w-4 h-4" /> PDF</a>
          {viewer.hasPptx && (
            <a href={`${API}/gout-ready/file/${viewer.token}?fmt=pptx&download=1`} data-testid="gr-download-pptx"
              className="inline-flex items-center gap-2 rounded-full bg-cream/10 hover:bg-cream/20 px-4 py-2 text-sm transition-colors"><Download className="w-4 h-4" /> PPTX</a>
          )}
          <button onClick={onClose} data-testid="gr-viewer-close" className="w-9 h-9 rounded-full bg-cream/10 hover:bg-cream/20 flex items-center justify-center transition-colors"><X className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="flex-1 relative bg-white">
        {!loaded && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-navy/40">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-sm">Cargando presentación…</span>
          </div>
        )}
        <iframe title={viewer.title} src={pdfUrl} onLoad={() => setLoaded(true)} className="w-full h-full bg-white" />
      </div>
    </motion.div>
  );
}
